import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { ModalPanel } from '@els/els-react--modal-panel';
import SearchTipsModalContent from '@Pages/QuickSearch/Components/BuildQuery/SearchTips/SearchTipsModalContent/SearchTipsModalContent';
import { SUMMARY_STATUS, SUMMARY_SCOPE, SUMMARY_SELECTION_ERROR, isSummarySelectionValid } from './constants';
import { getSummaries, getSummaryAttempt } from './selectors';
import {
  generateSummary as generateSummaryAction,
  cancelSummaryGeneration as cancelSummaryGenerationAction,
  limitToReferencedDocuments as limitToReferencedDocumentsAction,
  limitToReferencedReactions as limitToReferencedReactionsAction,
  copySummaryToClipboard as copySummaryToClipboardAction,
  submitSummaryFeedback as submitSummaryFeedbackAction,
} from './actions';
import useSummaryContainer from './hooks/useSummaryContainer';
import useSummaryLifecycleNavigation from './hooks/useSummaryLifecycleNavigation';
import { isSummaryContextStale } from './hooks/useEntryStaleness';
import { ASSISTANT_FEATURES, trackAssistantFeatureClick } from './analytics';
import SummarySection from './components/SummarySection';
import SummaryLoading from './components/SummaryLoading';
import SummaryError from './components/SummaryError';
import Summary from './components/Summary';
import AssistantOnboarding from './components/AssistantOnboarding';
import StaleSummaryNudge from './components/StaleSummaryNudge';
import SummaryTrailingActions from './components/SummaryTrailingActions';
import { resolveSummaryChain, resolveSummaryRequestParams } from './summaryContext';

import './SummaryContainer.scss';

function getSelectedResultsAnnouncement(selectedItems) {
  const selectedResultsCount = selectedItems?.length || 0;
  const resultLabel = selectedResultsCount === 1 ? 'result' : 'results';

  return `Summarising ${selectedResultsCount} selected ${resultLabel}.`;
}

export function SummaryContainer({
  activeBreadcrumb,
  params,
  selectedItems,
  chainKey,
  summaries,
  attempt,
  generateSummary,
  cancelSummaryGeneration,
  limitToReferencedDocuments,
  limitToReferencedReactions,
  copySummaryToClipboard,
  submitSummaryFeedback,
  onBeforeReferenceNavigation,
}) {
  const { isSummaryAvailable, howItWorksPanelVisible, toggleHowItWorksPanel } = useSummaryContainer({ activeBreadcrumb });
  const [selectionError, setSelectionError] = useState(null);
  const [generationAnnouncement, setGenerationAnnouncement] = useState('');
  const latestEntryRef = useRef(null);
  const previousAttemptRef = useRef(null);
  const stopButtonRef = useRef(null);
  const regenerateButtonRef = useRef(null);

  const latestSummary = summaries.length ? summaries[summaries.length - 1] : null;
  const isLoading = attempt?.status === SUMMARY_STATUS.LOADING;
  const latestIsError = attempt?.status === SUMMARY_STATUS.ERROR;

  useSummaryLifecycleNavigation({
    enabled: isSummaryAvailable,
    attempt,
    latestSummaryId: latestSummary?.id,
    latestEntryRef,
    stopButtonRef,
    regenerateButtonRef,
  });

  useEffect(() => {
    if (isSummarySelectionValid(selectedItems)) {
      setSelectionError(null);
    }
  }, [selectedItems]);

  useEffect(() => {
    const previousAttempt = previousAttemptRef.current;

    if (isLoading && (previousAttempt?.id !== attempt.id || previousAttempt.status !== SUMMARY_STATUS.LOADING)) {
      setGenerationAnnouncement(
        attempt.scope === SUMMARY_SCOPE.SELECTED ? getSelectedResultsAnnouncement(selectedItems) : 'Summarising results.'
      );
    } else if (previousAttempt?.status === SUMMARY_STATUS.LOADING) {
      if (latestIsError && attempt.id === previousAttempt.id) {
        setGenerationAnnouncement(attempt.cancelled ? 'Summary generation stopped.' : 'Summary could not be generated.');
      } else if (!attempt && latestSummary?.id === previousAttempt.id) {
        setGenerationAnnouncement('Summary generated.');
      }
    }

    previousAttemptRef.current = attempt;
  }, [attempt, isLoading, latestIsError, latestSummary?.id, selectedItems]);

  if (!isSummaryAvailable) {
    return null;
  }

  const requestParams = resolveSummaryRequestParams({ activeBreadcrumb, params, chainKey });
  const trackingContext = activeBreadcrumb?.context ?? params?.context;
  const { resultName, sort, order, pageNumber } = requestParams;
  const currentResultContext = { ...activeBreadcrumb, resultName, sort, order, pageNumber };

  const latestIsStale = latestSummary && isSummaryContextStale(latestSummary, currentResultContext);

  const onSummarizeTop = () => {
    trackAssistantFeatureClick(ASSISTANT_FEATURES.SUMMARIZE_RESULTS, trackingContext);
    generateSummary({ ...requestParams, scope: SUMMARY_SCOPE.TOP, trackingBreadcrumb: activeBreadcrumb });
  };

  const onRegenerate = () => {
    trackAssistantFeatureClick(ASSISTANT_FEATURES.REGENERATE_SUMMARY, trackingContext);
    generateSummary({ ...requestParams, scope: SUMMARY_SCOPE.TOP, trackingBreadcrumb: activeBreadcrumb });
  };

  const onSummarizeSelected = (items = selectedItems) => {
    trackAssistantFeatureClick(ASSISTANT_FEATURES.SUMMARIZE_SELECTED, trackingContext);
    if (!isSummarySelectionValid(items)) {
      setSelectionError(SUMMARY_SELECTION_ERROR);
      return false;
    }

    setSelectionError(null);
    generateSummary({ ...requestParams, scope: SUMMARY_SCOPE.SELECTED, selectedItems: items, trackingBreadcrumb: activeBreadcrumb });
    return true;
  };

  const selectionErrorType = selectionError ? 'warn' : 'secondary';
  const isSingleSummary = summaries.length === 1;
  const successActions = isSingleSummary
    ? [
        {
          dataE2e: 'summarize-selected-results-button',
          isSelectionAction: true,
          label: 'Summarise selected results',
          onClick: () => onSummarizeSelected(),
          type: selectionErrorType,
        },
      ]
    : [
        {
          dataE2e: 'summarize-results-button',
          label: 'Summarise results',
          onClick: onSummarizeTop,
          type: 'primary',
        },
        {
          dataE2e: 'summarize-selected-results-button',
          isSelectionAction: true,
          label: 'Summarise selected results',
          onClick: () => onSummarizeSelected(),
          type: selectionErrorType,
        },
      ];
  const errorActions = [
    {
      buttonRef: regenerateButtonRef,
      dataE2e: 'regenerate-summary-button',
      label: 'Re-generate summary',
      onClick: onRegenerate,
      size: 'default',
      type: 'primary',
    },
    {
      dataE2e: 'summarize-selected-results-button',
      isSelectionAction: true,
      label: 'Summarise selected results',
      onClick: () => onSummarizeSelected(),
      size: 'default',
      type: selectionErrorType,
    },
  ];

  return (
    <>
      {howItWorksPanelVisible && (
        <ModalPanel
          label="Learn how it works"
          panelPosition="right"
          modalProps={{
            label: 'Learn how it works',
            onClose: toggleHowItWorksPanel,
          }}
          className="c-els-modal--panel-custom">
          <SearchTipsModalContent withExamplesLinks={false} />
        </ModalPanel>
      )}
      <SummarySection aria-busy={isLoading}>
        {summaries.length === 0 && !attempt ? (
          <AssistantOnboarding
            selectedItems={selectedItems}
            onSummarizeTop={onSummarizeTop}
            onSummarizeSelected={onSummarizeSelected}
            selectionError={selectionError}
          />
        ) : (
          <>
            {summaries.map((entry, index) => {
              const isLatestSummary = entry.id === latestSummary?.id;
              const isActive = isLatestSummary && !isSummaryContextStale(entry, currentResultContext);

              return (
                <article
                  key={entry.id}
                  id={`summary-entry-${entry.id}`}
                  data-summary-id={entry.summaryId || undefined}
                  ref={!attempt && index === summaries.length - 1 ? latestEntryRef : undefined}
                  className="ai-search-summary__entry rx-focusable"
                  tabIndex={-1}>
                  {entry.summaryResponse && (
                    <Summary
                      entry={entry}
                      isActive={isActive}
                      isFirstSummary={index === 0}
                      activeBreadcrumb={currentResultContext}
                      params={params}
                      chainKey={chainKey}
                      showDisclosure={isLatestSummary}
                      limitToReferencedDocuments={limitToReferencedDocuments}
                      limitToReferencedReactions={limitToReferencedReactions}
                      copySummaryToClipboard={copySummaryToClipboard}
                      submitSummaryFeedback={submitSummaryFeedback}
                      onLearnHowItWorks={toggleHowItWorksPanel}
                      onBeforeReferenceNavigation={onBeforeReferenceNavigation}
                    />
                  )}
                </article>
              );
            })}

            {attempt && (
              <div
                key={attempt.id}
                id={`summary-entry-${attempt.id}`}
                data-summary-id={attempt.summaryId || undefined}
                ref={latestEntryRef}
                className="ai-search-summary__entry">
                {isLoading && (
                  <SummaryLoading
                    buttonRef={stopButtonRef}
                    onCancel={() => cancelSummaryGeneration({ chainKey, context: trackingContext })}
                  />
                )}
                {latestIsError && <SummaryError entry={attempt} />}
              </div>
            )}

            {/* FR-1.11: stale-summary nudge replaces the trailing CTA, not stacked above it */}
            {!attempt && latestIsStale && (
              <StaleSummaryNudge
                entry={latestSummary}
                activeBreadcrumb={currentResultContext}
                onSummarizeTop={onSummarizeTop}
                onSummarizeSelected={onSummarizeSelected}
                selectionError={selectionError}
              />
            )}

            {latestIsError && (
              <SummaryTrailingActions
                actions={errorActions}
                selectionError={selectionError}
                showIcon={false}
                testId="summary-error-actions"
                variant="actions-only"
              />
            )}

            {/* FR-1.12: regenerate is error-recovery only — no success state offers it */}
            {!attempt && !latestIsStale && latestSummary && (
              <SummaryTrailingActions
                actions={successActions}
                body={isSingleSummary ? 'I can give you an overview of the result set.' : undefined}
                heading={isSingleSummary ? 'Need help?' : undefined}
                selectionError={selectionError}
                showIcon={isSingleSummary}
              />
            )}
          </>
        )}
      </SummarySection>
      <div role="status" aria-atomic="true" className="u-els-hide-visually" data-testid="summary-generation-status">
        {generationAnnouncement}
      </div>
    </>
  );
}

const mapStateToProps = (state, { activeBreadcrumb, params }) => {
  const { chainKey } = resolveSummaryChain(activeBreadcrumb, params?.databaseId);

  return {
    chainKey,
    summaries: getSummaries(state, chainKey),
    attempt: getSummaryAttempt(state, chainKey),
  };
};

const mapDispatchToProps = (dispatch) => ({
  generateSummary: (...args) => dispatch(generateSummaryAction(...args)),
  cancelSummaryGeneration: (...args) => dispatch(cancelSummaryGenerationAction(...args)),
  limitToReferencedDocuments: (...args) => dispatch(limitToReferencedDocumentsAction(...args)),
  limitToReferencedReactions: (...args) => dispatch(limitToReferencedReactionsAction(...args)),
  copySummaryToClipboard: (...args) => dispatch(copySummaryToClipboardAction(...args)),
  submitSummaryFeedback: (...args) => dispatch(submitSummaryFeedbackAction(...args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SummaryContainer);
